import { useState, useEffect, useCallback, useRef } from 'react';
import { getSummaryHotEventClusterCategories } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

export interface ClusterCategoryOption {
  text: string;
  textEn: string;
  value: string;
}

const buildKey = (filter: any, sentimentValue: string) =>
  `${filter?.start_time}_${filter?.end_time}_${sentimentValue}`;

/**
 * Hot Event Cluster 分类筛选 Hook
 *
 * - 暴露 reset() 供外部在修改 filter/sentimentValue 时同步调用，利用 React 18 批处理避免 list_v2 二次请求
 * - 内部 useEffect 兜底 onFilterDateSync 等通过 Redux dispatch 触发的 filter 变化场景
 */
export const useClusterCategories = (
  gameInfo: any,
  filter: any,
  sentimentValue: string,
) => {
  const [clusterCategoriesValue, setClusterCategoriesValue] =
    useState<string>('all');
  const [clusterCategoryOptions, setClusterCategoryOptions] = useState<
    ClusterCategoryOption[]
  >([]);
  const { loading: clusterCategoriesLoading, wrapperCancelRequest } =
    useCancelRequestHook();

  const prevKeyRef = useRef<string>('');
  const initedRef = useRef(false);

  const fetchClusterCategories = useCallback(
    (overrideFilter?: any, overrideSentiment?: string) => {
      const effectiveFilter = overrideFilter || filter;
      const effectiveSentiment = overrideSentiment ?? sentimentValue;
      setClusterCategoryOptions([]);
      if (!gameInfo?.edition_unified_id || !effectiveFilter) return;

      const params = {
        ...gameInfo,
        sentiment_type: effectiveSentiment,
        channel_type: 'total',
        filter_keyword_response: true,
        ...(effectiveFilter || {}),
      };

      const currentKey = buildKey(effectiveFilter, effectiveSentiment);
      if (currentKey === prevKeyRef.current) return;
      prevKeyRef.current = currentKey;

      wrapperCancelRequest(getSummaryHotEventClusterCategories, params).then(
        (res: any) => {
          const resData = res?.data || res || {};
          const categories: Array<{
            label_zh: string;
            label_en: string;
            value: string;
          }> = resData.cluster_categories || [];

          const options: ClusterCategoryOption[] = categories.map(item => ({
            text: item.label_zh,
            textEn: item.label_en,
            value: item.value,
          }));

          setClusterCategoryOptions(options);
        },
      );
    },
    [gameInfo, filter, sentimentValue, wrapperCancelRequest],
  );

  /**
   * 重置为全部并加载分类。
   * 外部在 setFilter / setSentimentValue 的同事件处理器中调用，
   * React 18 自动批处理，只触发一次渲染 → 一次 list_v2 请求。
   */
  const reset = useCallback(
    (overrideFilter?: any, overrideSentiment?: string) => {
      setClusterCategoriesValue('all');
      fetchClusterCategories(overrideFilter, overrideSentiment);
    },
    [fetchClusterCategories],
  );

  // 首次 filter 不为 null 时自动加载
  useEffect(() => {
    if (!filter || initedRef.current) return;
    initedRef.current = true;
    reset(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // 兜底：onFilterDateSync 等通过 Redux dispatch 触发 filter 变化的场景
  // reset() 已更新 prevKeyRef，此处自动跳过，不会重复请求
  useEffect(() => {
    if (!filter || !initedRef.current) return;
    reset(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sentimentValue]);

  return {
    clusterCategoriesValue,
    setClusterCategoriesValue,
    clusterCategoryOptions,
    clusterCategoriesLoading,
    reset,
  };
};
